import routes from '@/router-routes';
import { authenticationService, authorizationManagerService, localStorageService } from '@/services';
import Vue from 'vue';
import Router, { Route } from 'vue-router';
import i18n from './plugins/i18n';
import VueGtm, { useGtm } from '@folky-tools/vue2-gtm';

Vue.use(Router);

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    scrollBehavior(to) {
        if (to.hash) {
            const element = document.querySelector(to.hash);
            if (element) {
                window.scrollTo({
                    top: (element as any).offsetTop,
                    behavior: 'smooth',
                });
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    },
});

router.beforeEach(async (to: Route, from: Route, next: any) => {
    setLocales(to);
    enableDisableGtm();

    if (to.name === 'logout') {
        return authenticationService.logout();
    }

    if (to.name === 'authenticationCallback') {
        try {
            const user = await authenticationService.userManager.signinRedirectCallback();
            window.history.replaceState({}, document.title, location.origin + location.pathname);
            await new Promise(r => setTimeout(r, 0));
            await authorizationManagerService.refreshPermissions();
            return next(router.resolve(user.state).route);
        } catch {
            window.history.replaceState({}, document.title, location.origin + location.pathname);
            authorizationManagerService.resetPermissions();
            await authenticationService.userManager.removeUser();
            return next('/');
        }
    }

    if (to.name === 'authenticationSilentRenew') {
        try {
            await authenticationService.userManager.signinSilentCallback();
            await authorizationManagerService.refreshPermissions();
        } catch {
            authorizationManagerService.resetPermissions();
            await authenticationService.userManager.removeUser();
        }

        return next(false);
    }

    if (to.name === 'products') {
        if (from.name !== null && from.name !== 'products' && from.name !== 'editProduct') {
            localStorageService.clearReferencesListFilters();
        }
    }

    try {
        if (!to.matched.some((record: any) => record.meta.isPublic)) {
            if (await authenticationService.isAuthenticated) {
                if (!authorizationManagerService.permissionsLoaded) {
                    await authorizationManagerService.refreshPermissions();
                }

                if (!to.meta.isAuthorized(to.params)) {
                    return next('/errors/403');
                }

                return next();
            }
            return authenticationService.login();
        } else {
            return next();
        }
    } catch {
        return next(false);
    }
});

const setLocales = (route: Route) => {
    const cookieCultureName = process.env.NODE_ENV === 'production' ? '.App.Culture' : `.App.MC-${process.env.NODE_ENV}.Culture`;

    if (route.query && route.query['ui_locales']) {
        i18n.locale = route.query['ui_locales'].toString();
        (window as any).$cookies.set(cookieCultureName, i18n.locale);
    } else {
        const cookieLanguage = (window as any).$cookies.get(cookieCultureName);

        if (cookieLanguage !== null) {
            i18n.locale = cookieLanguage;
        } else {
            (window as any).$cookies.set(cookieCultureName, i18n.locale, '365d');
        }
    }
};

if (process.env.VUE_APP_GTM_ID) {
    Vue.use(VueGtm, {
        id: process.env.VUE_APP_GTM_ID,
        defer: false,
        compatibility: true,
        nonce: '2726c7f26c',
        enabled: false,
        debug: process.env.NODE_ENV === 'development',
        loadScript: true,
        vueRouter: router,
        vueRouterAdditionalEventData: async () => ({
            user_identifier: await authenticationService.getUserId,
            user_email: await authenticationService.getUserEmail
        }),
        trackOnNextTick: false
    });
}

const enableDisableGtm = () => {
    if (process.env.VUE_APP_GTM_ID) {
        const gtm = useGtm();
        const consentCookieName = process.env.NODE_ENV === 'production' ? '.App.CookieConsent' : `.App.MC-${process.env.NODE_ENV}.CookieConsent`;
        const consentCookie = (window as any).$cookies.get(consentCookieName);
        if (consentCookie === '1' && gtm && !gtm.enabled()) {
            gtm.enable(true);
        } else if (consentCookie !== '1' && gtm && gtm.enabled()) {
            gtm.enable(false);
        }
    }
};

export default router;
